import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import api from '../utils/api';

const BookingCancelled = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orgBranding, setOrgBranding] = useState({
    name: 'BookingFlows',
    primaryColor: '#2563EB',
    logoUrl: '',
    showPoweredByBookingFlows: true,
    companyWebsite: ''
  });
  const [countdown, setCountdown] = useState(5);
  const [serviceName, setServiceName] = useState('');

  const orgId = searchParams.get('org');
  const serviceId = searchParams.get('service');
  const date = searchParams.get('date');
  const time = searchParams.get('time');
  const tickets = searchParams.get('tickets');

  // Build the return URL to the widget
  const getWidgetUrl = () => {
    if (orgId) {
      let url = `/services-grid.html?org_id=${orgId}`;
      if (serviceId) url += `&service=${serviceId}`;
      if (date) url += `&date=${date}`;
      if (time) url += `&time=${time}`;
      if (tickets) url += `&tickets=${tickets}`;
      return url;
    }
    return null;
  };

  const widgetUrl = getWidgetUrl();

  useEffect(() => {
    const fetchOrgBranding = async () => {
      if (!orgId) return;
      try {
        const response = await api.get(`/public/organizations/${orgId}`);
        const data = response.data;
        if (data.organization) {
          setOrgBranding({
            name: data.organization.name || 'BookingFlows',
            primaryColor: data.organization.primaryColor || '#2563EB',
            logoUrl: data.organization.logoUrl || '',
            showPoweredByBookingFlows: data.organization.showPoweredByBookingFlows !== false,
            companyWebsite: data.organization.companyWebsite || data.organization.website || ''
          });
        }
      } catch (error) {
        // Use defaults
      }
    };
    
    const fetchServiceName = async () => {
      if (!serviceId || !orgId) return;
      try {
        const response = await api.get(`/public/services/${serviceId}?org_id=${orgId}`);
        if (response.data?.name) {
          setServiceName(response.data.name);
        }
      } catch (error) {
        // Use defaults
      }
    };
    
    fetchOrgBranding();
    fetchServiceName();
  }, [orgId, serviceId]);

  // Auto-redirect countdown
  useEffect(() => {
    if (!widgetUrl) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = widgetUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [widgetUrl]);

  const handleReturnToBooking = () => {
    if (widgetUrl) {
      window.location.href = widgetUrl;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header with brand color */}
          <div 
            className="px-6 py-5 text-center"
            style={{ backgroundColor: orgBranding.primaryColor }}
          >
            {orgBranding.logoUrl ? (
              <img 
                src={orgBranding.logoUrl} 
                alt={orgBranding.name}
                className="h-10 mx-auto mb-2 object-contain"
              />
            ) : (
              <h2 className="text-white font-semibold text-lg">{orgBranding.name}</h2>
            )}
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            {/* Cart Icon */}
            <div className="w-14 h-14 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-7 h-7 text-gray-400" />
            </div>

            <h1 className="text-lg font-semibold text-gray-900 mb-2">
              Checkout Not Completed
            </h1>
            
            <p className="text-sm text-gray-500 mb-1">
              No worries — you weren't charged.
            </p>
            
            {serviceName && (
              <p className="text-sm text-gray-600 mb-4">
                Your spot for <span className="font-medium">{serviceName}</span> is still available.
              </p>
            )}

            {/* Return Button */}
            {widgetUrl && (
              <button
                onClick={handleReturnToBooking}
                className="w-full mt-4 px-4 py-3 rounded-xl font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ backgroundColor: orgBranding.primaryColor }}
              >
                <ArrowLeft className="w-4 h-4" />
                Return to Booking
              </button>
            )}

            {/* Countdown */}
            {widgetUrl && countdown > 0 && (
              <p className="text-xs text-gray-400 mt-3">
                Redirecting in {countdown}s...
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-4">
          <a
            href={orgBranding.showPoweredByBookingFlows ? 'https://bookingflows.com' : (orgBranding.companyWebsite || '#')}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-600 transition-colors"
          >
            {orgBranding.showPoweredByBookingFlows ? (
              <>Powered by <span style={{ color: orgBranding.primaryColor }}>BookingFlows</span></>
            ) : (
              <>© <span style={{ color: orgBranding.primaryColor }}>{orgBranding.name}</span></>
            )}
          </a>
        </p>
      </div>
    </div>
  );
};

export default BookingCancelled;
